import { differenceInDays, parseISO, format } from "date-fns";

export interface PolicyStatus {
  status: "expiring_soon" | "active" | "expired";
  daysRemaining: number;
}

export interface PolicyDisplayStatus {
  type: "normal" | "valid_till" | "expired" | "start_from";
  displayDate?: string;
  daysRemaining?: number;
}

/**
 * Calculate the policy status based on the policy end date
 * @param policyEndDate - The end date of the policy
 * @returns PolicyStatus object containing status and days remaining
 */
export const getPolicyStatus = (policyEndDate: string): PolicyStatus => {
  const today = new Date();
  const endDate = parseISO(policyEndDate || "");
  const daysRemaining = differenceInDays(endDate, today);

  if (daysRemaining < 0) {
    return {
      status: "expired",
      daysRemaining: 0,
    };
  } else if (daysRemaining <= 30) {
    return {
      status: "expiring_soon",
      daysRemaining,
    };
  } else {
    return {
      status: "active",
      daysRemaining,
    };
  }
};

/**
 * Get the policy display status for the card UI
 * @param policyStartDate - The start date of the policy
 * @param policyEndDate - The end date of the policy
 * @returns PolicyDisplayStatus object containing type and display information
 */
export const getPolicyDisplayStatus = (
  policyStartDate: string,
  policyEndDate: string,
  status: string,
  renewalStartDate: string
): PolicyDisplayStatus => {
 
   

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
  
  const startDate = parseISO(policyStartDate || "");
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = parseISO(policyEndDate || "");
  endDate.setHours(0, 0, 0, 0);
  
  const daysUntilStart = differenceInDays(startDate, today);
  const daysUntilEnd = differenceInDays(endDate, today);

  // Policy hasn't started yet (start date is after current date)
  if(status === "UPCOMING"){
    return {
      type: "start_from",
      displayDate: format(parseISO(renewalStartDate), "dd MMM yyyy"),
    };
  }

  // if (daysUntilStart > 0) {
  //   return {
  //     type: "start_from",
  //     displayDate: format(startDate, "dd MMM yyyy"),
  //   };
  // }

  // Policy has expired (end date is before current date)
  if (daysUntilEnd < 0) {
    return {
      type: "expired",
    };
  }

  // Policy expiring within 30 days (less than one month)
  if (daysUntilEnd <= 30) {
    return {
      type: "valid_till",
      displayDate: format(endDate, "dd MMM yyyy"),
      daysRemaining: daysUntilEnd,
    };
  }

  // Policy is active and has more than 30 days remaining
  return {
    type: "normal",
    daysRemaining: daysUntilEnd,
  };
};

/**
 * Format the renewal price
 * @param price - The price in number format
 * @returns Formatted price string
 */
export const formatRenewalPrice = (price: number): string => {
  return `₹${price}/yearly`;
};

/**
 * Transform user data from the received format to the API format for renewal
 * @param data - The user data object received from the backend
 * @returns Transformed data object ready for the renewal API
 */
export const transformUserDataForRenewal = (data: any) => {
  // Convert ISO date to DD-MM-YYYY format
  const formatDateOfBirth = (isoDate: string) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Capitalize gender (MALE -> Male, FEMALE -> Female)
  const capitalizeGender = (gender: string) => {
    return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
  };

  return {
    firstName: data.firstName,
    lastName: data.lastName,
    gender: capitalizeGender(data.gender),
    mobileNo: data.mobileNo,
    email: data.email,
    address: data.address,
    pinCode: String(data.pinCode),
    dateOfBirth: formatDateOfBirth(data.dateOfBirth),
    prosureRenewalFlow:true,
    prosureOldPolicyCardStatus:cardStatus(data.policyEndDate),
    oldProsureCustomerId:data.prosureCustomerId,
    agentId: "CSRenewalUser" // Hardcoded as per requirement
  };
};



function cardStatus(policyEndDate: string){
  const today = new Date();
  const endDate = parseISO(policyEndDate || "");
  endDate.setHours(0, 0, 0, 0);
  const daysRemaining = differenceInDays(endDate, today);
  if (daysRemaining < 0) {
    return "EXPIRED";
  } else {
    return "ACTIVE";
  }
}

 